import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import { Box, ButtonV2 } from '@bridebook/ui';
import ContentContainer from '@bridebook/web/app-basic/components/supplier/content-container';
import Experience from '@bridebook/web/app-basic/components/supplier/sections/experience/experience';
import PersonalMessage from '@bridebook/web/app-basic/components/supplier/sections/personal-message/personal-message';
import SectionText from '@bridebook/web/app-shared/components/supplier/sections/section-text/section-text';
import SupplierAccordion from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import { supplierDetailsList } from '@bridebook/web/app-shared/lib/supplier/supplier-details-list';
import { fullDescriptionVisibility } from '@bridebook/web/app-shared/lib/supplier/utils/fullDescriptionVisibility';
import itemsDefined from '@bridebook/web/app-shared/lib/supplier/utils/items-defined';
import nonEmptyData from '@bridebook/web/app-shared/lib/supplier/utils/non-empty-data';
import { renderHtml } from '@bridebook/web/app-shared/lib/ui/html-renderer';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/description-full/description-full.style';
import { getScreenType } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSupplierAccordion } from '@bridebook/web/app-basic/components/supplier/hooks/use-supplier-accordion';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getIsVenue, getSupplier } from '@bridebook/web/app-basic/lib/supplier/selectors';

const DescriptionFull = () => {
  const supplier = useSelector(getSupplier);
  const accordionProps = useSupplierAccordion();
  const { isMobile } = useSelector(getScreenType);
  const isVenue = useSelector(getIsVenue);
  const maxLines = isVenue ? 4 : undefined;
  const descriptionFallback = isVenue ? supplier?.description?.short : undefined;
  const { t } = useTranslation('supplier');
  const [lineLimit, setLineLimit] = useState(maxLines ?? 0);
  const expandOrCollapse = useCallback(
    () => setLineLimit((limit) => (limit > 0 ? 0 : (maxLines ?? 0))),
    [maxLines],
  );
  const supplierName = supplier?.name;

  const variant = useMemo(() => {
    if (isVenue) return 'h2';
  }, [isVenue]);

  const text = useMemo(() => {
    if (isVenue) return t('heading.description');
    return t('fullDescriptionWithName', { supplierName });
  }, [isVenue, supplierName, t]);

  if (!supplier || !fullDescriptionVisibility(supplier)) {
    return null;
  }

  const managerName = supplier.company?.manager?.name;
  const managerMessage = supplier.company?.manager?.welcomeMessage;

  const isMessage = Boolean(managerName) && Boolean(managerMessage);
  const items = itemsDefined(supplierDetailsList, supplier);
  const isExperience = nonEmptyData(items);
  const styles = componentStyles();
  const descriptionLong = supplier?.description?.long;
  const isCollapsed = lineLimit > 0;
  const isDescriptionBordered = !isCollapsed && (isMessage || isExperience);
  const showExpandOrCollapseButton = maxLines !== undefined && maxLines > 0;
  const description = renderHtml(descriptionLong || descriptionFallback || '', {
    reducedSpacing: true,
  });

  return (
    <Box>
      <SupplierAccordion
        {...accordionProps}
        openStatic
        name="description-accordion"
        id="supplier-description-full"
        horizontalMargins={isMobile}
        text={text}
        variant={variant}>
        <ContentContainer noPaddingBottom={isCollapsed && showExpandOrCollapseButton}>
          <Box style={styles.descriptionWrap(isDescriptionBordered)}>
            <Box style={styles.descriptionRichText}>
              <SectionText lineHeight={30} clampTo={lineLimit} noBlock>
                {description}
              </SectionText>
            </Box>
            {isCollapsed && (
              <ButtonV2
                onClick={expandOrCollapse}
                styleWrapper={styles.buttonWrap(showExpandOrCollapseButton, isCollapsed)}
                variant="link">
                {t('button.readMore')}
              </ButtonV2>
            )}
          </Box>
          {isMessage && (
            <Box style={styles.messageWrap(!isCollapsed)}>
              <PersonalMessage managerName={managerName} managerMessage={managerMessage} />
            </Box>
          )}
        </ContentContainer>
        {isExperience && (
          <Box style={styles.experienceWrap(!isCollapsed, !showExpandOrCollapseButton)}>
            <Experience supplier={supplier} />
          </Box>
        )}
        {!isCollapsed && (
          <ButtonV2
            onClick={expandOrCollapse}
            styleWrapper={styles.buttonWrap(showExpandOrCollapseButton, isCollapsed)}
            variant="link">
            {t('button.seeLess')}
          </ButtonV2>
        )}
      </SupplierAccordion>
    </Box>
  );
};

export default DescriptionFull;
